/** The dashboard's layout decisions, kept out of the components so node-only vitest can pin them.
 *  ⛔ No pay, salary or bank figure belongs here; the restricted `payroll` and `finance` blocks are not a source. */

import type { SlideWeight } from '../../lib/autoSlide'
import { NON_WORKING_MODES } from '../../pages/time/timeData'

export type TileKey = 'whoIsIn' | 'desksFree' | 'awayToday'
export type SlideKey = 'org' | 'byTeam' | 'fund' | 'birthdays' | 'away'

/** The slots this viewer gets, decided by abilities alone so a slow query never moves the page when it lands. */
export function dashboardSlots(abilities: { staff: boolean; hrView: boolean }): {
  tiles: TileKey[]
  slides: SlideKey[]
} {
  // ⛔ The server sends work modes and leave as empty lists to a viewer without staff, so those slots would assert "nobody".
  const tiles: TileKey[] = abilities.staff ? ['whoIsIn', 'desksFree', 'awayToday'] : ['desksFree']
  // Away, the fund and birthdays stay adjacent in this order so all three pack onto one page where it fits.
  const slides: SlideKey[] = ['org', 'byTeam']
  if (abilities.staff) slides.push('away', 'fund')
  if (abilities.hrView) slides.push('birthdays')
  return { tiles, slides }
}

/** Column units each dashboard card needs; the carousel packs pages by these. */
export const SLIDE_WEIGHT: Record<SlideKey, SlideWeight> = {
  org: 2,
  byTeam: 2,
  fund: 1,
  birthdays: 1,
  away: 1,
}

/** Column classes for the tile strip: two columns on a phone, one row of three where three fit. */
export function tileGridClass(count: number): string {
  if (count === 1) return 'grid grid-cols-1 gap-3'
  return count >= 3 ? 'grid grid-cols-2 gap-3 @2xl:grid-cols-3' : 'grid grid-cols-2 gap-3'
}

/** The odd tile out on a two-column phone strip takes the whole row instead of leaving a hole beside it. */
export function tileSpanClass(index: number, count: number): string {
  return count > 1 && count % 2 === 1 && index === count - 1 ? 'col-span-2 @2xl:col-span-1' : ''
}

export type Known<T> = { state: 'loading' } | { state: 'error'; error: unknown } | { state: 'ready'; value: T }

/** The birthdays card: absent once the server says `people` is not this viewer's, never an empty list standing in for "withheld". */
export function birthdaysView<T>(input: {
  people: { birthdays: T[] } | null | undefined
  loaded: boolean
  error: unknown
}): Known<T[]> | { state: 'absent' } {
  if (input.error) return { state: 'error', error: input.error }
  if (!input.loaded) return { state: 'loading' }
  return input.people ? { state: 'ready', value: input.people.birthdays } : { state: 'absent' }
}

/** Today's working-mode counts, largest first; Off, Leave and Holiday are not "in", and people with no mode logged are not counted. */
export function modesToday(groups: { mode: string; people: string[] }[]): { total: number; modes: { mode: string; count: number }[] } {
  const modes = groups
    .filter((g) => g.people.length > 0 && !NON_WORKING_MODES.includes(g.mode))
    .map((g) => ({ mode: g.mode, count: g.people.length }))
    .sort((a, b) => b.count - a.count || a.mode.localeCompare(b.mode))
  return { total: modes.reduce((sum, m) => sum + m.count, 0), modes }
}
